import React, { useEffect, useState } from 'react';
import { get } from '../utils/api';

const fmt = n => new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0 }).format(Math.round(n || 0)) + ' ₽';
const fmtPct = (a, b) => b > 0 ? (a / b * 100).toFixed(1) + '%' : '—';
const fmtDate = d => { if (!d) return d; const [y, m, day] = d.split('-'); return `${day}-${m}-${y.slice(2)}`; };
const SUBTYPE_LABEL = { dividend_ak: 'Алексей Комаров', dividend_dominant: 'Доминант' };

function Block({ title, hint, d, shares }) {
  const akColor  = d.akDelta >= 0 ? 'var(--red)' : 'var(--green)';
  const domColor = d.dominantDelta >= 0 ? 'var(--red)' : 'var(--green)';
  const rows = [
    { name: 'Алексей Комаров', share: shares.ak,       actual: d.ak,       expected: d.akExpected,       delta: d.akDelta,       color: akColor },
    { name: 'Доминант',        share: shares.dominant, actual: d.dominant, expected: d.dominantExpected, delta: d.dominantDelta, color: domColor },
  ];
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="section-title" style={{ marginBottom: 12 }}>
        {title} — всего выплачено {fmt(d.total)}
        {hint && <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 12, color: 'var(--text3)' }}>{hint}</span>}
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Акционер</th>
              <th style={{ textAlign: 'right' }}>Доля</th>
              <th style={{ textAlign: 'right' }}>Фактически</th>
              <th style={{ textAlign: 'right' }}>% по факту</th>
              <th style={{ textAlign: 'right' }}>По доле</th>
              <th style={{ textAlign: 'right' }}>Дисбаланс</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.name}>
                <td style={{ fontWeight: 600 }}>{r.name}</td>
                <td style={{ textAlign: 'right' }}>{Math.round(r.share * 100)}%</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.actual)}</td>
                <td style={{ textAlign: 'right', color: 'var(--text2)' }}>{fmtPct(r.actual, d.total)}</td>
                <td style={{ textAlign: 'right', color: 'var(--text3)' }}>{fmt(r.expected)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: r.color }}>
                  {r.delta >= 0 ? '+' : ''}{fmt(r.delta)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d.total > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
          «Дисбаланс» = фактически — по доле. Положительный = акционер взял больше своей доли; отрицательный = недобрал.
        </div>
      )}
    </div>
  );
}

export default function DividendsReportPage() {
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01-01`);
  const [to, setTo]     = useState(now.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await get(`/reports/dividends?from=${from}&to=${to}`); setData(d); }
    catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [from, to]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Дивиденды</h1>
      </div>

      <div className="filters" style={{ marginBottom: 24 }}>
        <div className="form-group"><label>С</label><input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group"><label>По</label><input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          {[
            { label: String(now.getFullYear()),     f: `${now.getFullYear()}-01-01`,   t: `${now.getFullYear()}-12-31` },
            { label: String(now.getFullYear() - 1), f: `${now.getFullYear() - 1}-01-01`, t: `${now.getFullYear() - 1}-12-31` },
          ].map((p, i) => (
            <button key={i} className="btn btn-ghost btn-sm" onClick={() => { setFrom(p.f); setTo(p.t); }}>{p.label}</button>
          ))}
        </div>
      </div>

      {loading && <p style={{ color: 'var(--text3)' }}>Загрузка...</p>}

      {!loading && data && (<>
        <Block title="За период" d={data.period} shares={data.shares} />
        <Block title="Итого на сейчас" hint={`накопительно по ${fmtDate(data.asOf)}`} d={data.cumulative} shares={data.shares} />

        <div className="card">
          <div className="section-title" style={{ marginBottom: 16 }}>Выплаты за период ({data.items.length})</div>
          {data.items.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Нет выплат в этом периоде</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Получатель</th>
                    <th style={{ textAlign: 'right' }}>Сумма</th>
                    <th>Способ</th>
                    <th>Описание</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(t => (
                    <tr key={t.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.date)}</td>
                      <td>{SUBTYPE_LABEL[t.subtype] || t.subtype}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(t.amount)}</td>
                      <td>{t.payment_method}</td>
                      <td style={{ fontSize: 12, color: 'var(--text3)' }}>{t.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>)}
    </div>
  );
}
